import React, { useState, useEffect } from 'react';
import { 
  ArrowLeft, Save, Building, Mail, Phone, FileText, 
  AlertCircle, X, Check, Handshake, MapPin, Euro
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL;

export default function NewProvider() {
  // Estados del formulario
  const [formData, setFormData] = useState({
    nombre: '',
    direccion: '',
    telefono: '',
    email: '',
    precio_min: ''
  });

  // Estados de UI
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [errors, setErrors] = useState({});

  // Manejar cambios en el formulario
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Limpiar error del campo
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Validar formulario
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.nombre.trim()) newErrors.nombre = 'El nombre es requerido';
    
    // Validar email principal
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'El email no es válido';
    }

    // Validar teléfono (si se proporciona)
    if (formData.telefono && !/^\+?[\d\s\-()]+$/.test(formData.telefono)) {
      newErrors.telefono = 'El teléfono no tiene un formato válido';
    }

    // Validar precio mínimo (si se proporciona)
    if (formData.precio_min && (isNaN(formData.precio_min) || parseFloat(formData.precio_min) < 0)) {
      newErrors.precio_min = 'El precio mínimo debe ser un número válido mayor o igual a 0';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Enviar formulario
  const handleSubmit = async () => {
    if (!validateForm()) {
      showMessage('Por favor corrige los errores en el formulario', 'danger');
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      
      const providerData = {
        nombre: formData.nombre.trim(),
        direccion: formData.direccion.trim(),
        telefono: formData.telefono.trim(),
        email: formData.email.trim(),
        precio_min: formData.precio_min ? parseFloat(formData.precio_min) : null
      };

      const response = await fetch(`${API_URL}/api/proveedores`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(providerData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al crear proveedor');
      }

      showMessage('Proveedor creado correctamente', 'success');
      
      // Limpiar formulario
      setFormData({
        nombre: '',
        direccion: '',
        telefono: '',
        email: '',
        precio_min: ''
      });

      // Redirigir después de un momento
      setTimeout(() => {
        window.location.href = '?sec=proveedores/lista';
      }, 2000);

    } catch (error) {
      console.error('Error al crear proveedor:', error);
      showMessage(error.message || 'Error al crear el proveedor', 'danger');
    } finally {
      setSaving(false);
    }
  };

  // Utilidades
  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 8080);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="shadow-lg border-b-2 bg-white" style={{borderBottomColor: '#4DD5AF'}}>
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center shadow-lg" style={{backgroundColor: '#4DD5AF'}}>
                <Handshake className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-800">Nuevo Proveedor</h1>
                <p className="text-sm font-medium" style={{color: '#4DD5AF'}}>Registrar nuevo proveedor</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Info Bar */}
      <div className="text-white" style={{background: `linear-gradient(to right, #4DD5AF, #34d399, #10b981)`}}>
        <div className="max-w-4xl mx-auto px-6 py-3">
          <div className="flex items-center space-x-2">
            <Building className="w-4 h-4" />
            <span className="font-bold">Formulario de Registro de Proveedor</span>
          </div>
        </div>
      </div>

      {/* Mensaje de estado */}
      {message.text && (
        <div className="max-w-4xl mx-auto px-6 pt-4">
          <div className={`p-3 rounded-lg flex items-center shadow-lg border text-sm ${
            message.type === 'success' ? 'border-emerald-200' : 'border-red-200'
          }`}
          style={{
            background: message.type === 'success' ? 'linear-gradient(135deg, #ecfdf5, #d1fae5)' : 'linear-gradient(135deg, #fef2f2, #fecaca)',
            color: message.type === 'success' ? '#065f46' : '#991b1b'
          }}>
            {message.type === 'success' ? (
              <Check className="w-4 h-4 mr-2 flex-shrink-0" />
            ) : (
              <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
            )}
            <span className="font-medium flex-1">{message.text}</span>
            <button 
              onClick={() => setMessage({ text: '', type: '' })}
              className="ml-auto p-1 hover:bg-white hover:bg-opacity-30 rounded"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        </div>
      )}

      {/* Formulario */}
      <div className="max-w-4xl mx-auto px-6 py-6">
        <div className="bg-white rounded-xl shadow-lg border-2" style={{borderColor: '#4DD5AF'}}>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              
              {/* Nombre del Proveedor */}
              <div className="md:col-span-2">
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  <Building className="w-4 h-4 inline mr-2" />
                  Nombre del Proveedor*
                </label>
                <input
                  type="text"
                  value={formData.nombre}
                  onChange={(e) => handleInputChange('nombre', e.target.value)}
                  placeholder="Nombre o razón social del proveedor"
                  className={`w-full px-3 py-2 border-2 rounded-lg focus:outline-none transition-all ${
                    errors.nombre ? 'border-red-500' : 'border-gray-300'
                  }`}
                  onFocus={(e) => !errors.nombre && (e.target.style.borderColor = '#4DD5AF')}
                  onBlur={(e) => !errors.nombre && (e.target.style.borderColor = '#e5e7eb')}
                />
                {errors.nombre && <p className="text-red-500 text-xs mt-1">{errors.nombre}</p>}
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  <Mail className="w-4 h-4 inline mr-2" />
                  Email
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="correo@proveedor.com"
                  className={`w-full px-3 py-2 border-2 rounded-lg focus:outline-none transition-all ${
                    errors.email ? 'border-red-500' : 'border-gray-300'
                  }`}
                  onFocus={(e) => !errors.email && (e.target.style.borderColor = '#4DD5AF')}
                  onBlur={(e) => !errors.email && (e.target.style.borderColor = '#e5e7eb')}
                />
                {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
              </div>

              {/* Teléfono */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  <Phone className="w-4 h-4 inline mr-2" />
                  Teléfono
                </label>
                <input
                  type="tel"
                  value={formData.telefono}
                  onChange={(e) => handleInputChange('telefono', e.target.value)}
                  placeholder="+34 912 345 678"
                  className={`w-full px-3 py-2 border-2 rounded-lg focus:outline-none transition-all ${
                    errors.telefono ? 'border-red-500' : 'border-gray-300'
                  }`}
                  onFocus={(e) => !errors.telefono && (e.target.style.borderColor = '#4DD5AF')}
                  onBlur={(e) => !errors.telefono && (e.target.style.borderColor = '#e5e7eb')}
                />
                {errors.telefono && <p className="text-red-500 text-xs mt-1">{errors.telefono}</p>}
              </div>

              {/* Precio Mínimo */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  <Euro className="w-4 h-4 inline mr-2" />
                  Precio Mínimo (€)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.precio_min}
                  onChange={(e) => handleInputChange('precio_min', e.target.value)}
                  placeholder="0.00"
                  className={`w-full px-3 py-2 border-2 rounded-lg focus:outline-none transition-all ${
                    errors.precio_min ? 'border-red-500' : 'border-gray-300'
                  }`}
                  onFocus={(e) => !errors.precio_min && (e.target.style.borderColor = '#4DD5AF')}
                  onBlur={(e) => !errors.precio_min && (e.target.style.borderColor = '#e5e7eb')}
                />
                {errors.precio_min && <p className="text-red-500 text-xs mt-1">{errors.precio_min}</p>}
              </div>

              {/* Dirección */}
              <div className="md:col-span-2">
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  <MapPin className="w-4 h-4 inline mr-2" />
                  Dirección
                </label>
                <textarea
                  value={formData.direccion}
                  onChange={(e) => handleInputChange('direccion', e.target.value)}
                  placeholder="Dirección completa del proveedor"
                  rows={3}
                  className="w-full px-3 py-2 border-2 rounded-lg focus:outline-none transition-all border-gray-300 resize-vertical"
                  onFocus={(e) => e.target.style.borderColor = '#4DD5AF'}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                />
              </div>
            </div>

            {/* Botones */}
            <div className="flex justify-center gap-4 mt-8">
              <button
                onClick={handleSubmit}
                disabled={saving}
                className={`px-6 py-3 rounded-lg font-medium transition-all flex items-center text-white ${
                  saving ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-90'
                }`}
                style={{backgroundColor: '#4DD5AF'}}
              >
                {saving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Creando...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Crear Proveedor
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}